import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { ConfirmationComplaint } from '../models/confirmation-complaint-model';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ConfirmationExternalComplaintModel,
  CreditNoteModel,
  ValidateCreditNoteRequest,
  ValidateCreditNotesResult,
} from '../models/confirmation-external-complaint-model';
import { ComplaintCreditNoteCharge } from '../models/charge-type-model';

@Injectable({
  providedIn: 'root'
})
export class ConfirmationComplaintService {

  private API_CONFIRMATION_COMPLAINT_URL = environment.ipserver + 'CnC-backend/private/complaintEntry/confirmationComplaintEntry';

  private API_CONFIRMATION_EXTERNAL_URL = environment.ipserver + 'CnC-backend/private/complaintEntry/findOrdersLinesforConfirmation';

  private API_CONFIRMATION_COMPLAINT_EXTERNAL_URL = environment.ipserver + 'CnC-backend/private/complaintEntry/confirmationExternalComplaintEntry';

  private API_CONFIRMATION_ADDON_COMPLAINT_EXTERNAL_URL = environment.ipserver + 'CnC-backend/private/complaintEntry/confirmationAddOnComplaintEntry';

  private API_VALIDATE_PROCESSES_CONFIRMATION_URL = environment.ipserver + 'CnC-backend/private/complaintEntry/currentProcessVendorType';

  private API_VALIDATE_INVOICE_FOR_CREDIT_NOTE_URL = environment.ipserver + 'CnC-backend/private/complaintConfirmation/validateInvoiceForCreditNote';

  private API_VALIDATE_CREDIT_NOTE_URL = environment.ipserver + 'CnC-backend/private/complaintConfirmation/validateCreditNote';
  
  private API_CREATE_CREDIT_NOTE_FROM_INVOICE_URL = environment.ipserver + 'CnC-backend/private/complaintConfirmation/createCreditNoteFromInvoice';
  
  private API_SEND_ERROR_ROSS_EMAIL_URL = environment.ipserver + 'CnC-backend/private/complaintConfirmation/sendErrorROSSEmail';

  private API_CREATE_INVOICE_URL = environment.ipserver + 'CnC-backend/private/complaintConfirmation/createInvoice';

  private API_MISCELLANEOUS_ADJUSTMENT_CODES_URL = environment.ipserver + 'CnC-backend/private/miscellaneousAdjustment/getMiscelaneousAdjustmentCodes';

  private API_GET_PALLETS_DATA_BY_COMPLAINT_ID = environment.ipserver + 'CnC-backend/private/complaintPallet/getPalletsDataByComplaintId';

  constructor(private http: HttpClient) { }

  /**
  * Method that updates disposition option by calling applicatios rest service
  * @author Ana Quiñones
  * @since 2024-06-11
  * @return Observable with a json 
  */
  public updateConfirmationComplaint(data: {confirmationComplaint: ConfirmationComplaint, attachments: any[],
    files?: File[]
  }): Observable<any> {
    const formData = new FormData();
  
    if (data.files && data.files.length > 0) {
      data.files.forEach((file, index) => {
        formData.append('files', file, file.name);
      });
    }
  
    if (data.confirmationComplaint) {
      const complaintDetailsStr = JSON.stringify(data.confirmationComplaint);
      formData.append('complaintHeaderDto', complaintDetailsStr);
    }

    if(data.attachments && data.attachments.length > 0) {
      const attachmentsStr = JSON.stringify(data.attachments);
      formData.append('attachments', attachmentsStr);
    }else{
      formData.append('attachments', JSON.stringify([]));
    }

    return this.http.put<any>(`${this.API_CONFIRMATION_COMPLAINT_URL}`, formData);
  }

  public updateConfirmationExternalComplaint(data: {confirmationExternalComplaint: ConfirmationExternalComplaintModel, attachments: any[],
    files?: File[]
  }): Observable<any> {

    const formData = new FormData();
  
    if (data.files && data.files.length > 0) {
      data.files.forEach((file, index) => {
        formData.append('files', file, file.name);
      });
    }
  
    if (data.confirmationExternalComplaint) {
      const complaintDetailsStr = JSON.stringify(data.confirmationExternalComplaint);
      formData.append('complaintHeaderDto', complaintDetailsStr);
    }

    if(data.attachments && data.attachments.length > 0) {
      const attachmentsStr = JSON.stringify(data.attachments);
      formData.append('attachments', attachmentsStr);
    }else{
      formData.append('attachments', JSON.stringify([]));
    }
    
    return this.http.put<any>(`${this.API_CONFIRMATION_COMPLAINT_EXTERNAL_URL}`, formData);
  }

  public updateConfirmationExternalAddOnComplaint(confirmationExternalComplaint: ConfirmationExternalComplaintModel): Observable<any> {
    const formData = new FormData();
    formData.append('complaintHeaderDto', JSON.stringify(confirmationExternalComplaint));
    return this.http.put<any>(`${this.API_CONFIRMATION_ADDON_COMPLAINT_EXTERNAL_URL}`, formData);
  }

  public getConfirmationComplaintExternal(complaintId: number): Observable<any> {
    const params = new HttpParams().set('complaintId', complaintId.toString());
    return this.http.get<any>(`${this.API_CONFIRMATION_EXTERNAL_URL}`, { params });
  }

  public getCurrentProcessVendorType(complaintId: number): Observable<any> {
    const params = new HttpParams().set('complaintId', complaintId.toString());
    return this.http.get<any>(this.API_VALIDATE_PROCESSES_CONFIRMATION_URL,  { params });
  }

  public validateInvoiceForCreditNote(complaintId: number, creditNoteId: number): Observable<any> {
    const params = new HttpParams()
      .set('complaintId', complaintId.toString())
      .set('creditNoteId', creditNoteId.toString());
    return this.http.get<any>(this.API_VALIDATE_INVOICE_FOR_CREDIT_NOTE_URL, { params });
  }

  /**
   * Validates credit note lines before saving external confirmation.
   */
  public validateCreditNote(body: ValidateCreditNoteRequest): Observable<any> {
    return this.http.post<any>(this.API_VALIDATE_CREDIT_NOTE_URL, body);
  }

  public buildValidateCreditNoteRequest(
    idComplaint: number,
    creditNote: CreditNoteModel
  ): ValidateCreditNoteRequest {
    return {
      idComplaint,
      sequence: creditNote.sequence,
      creditNoteLines: (creditNote.creditNoteLines || []).map((line) => ({
        orderLineNumber: Number(line.orderLineNumber),
        partCode: String(line.partCode ?? ''),
        quantity: Number(line.quantity),
        price: Number(line.price),
      })),
    };
  }

  /**
   * Credit note already created in Ross (has a number); skip validateCreditNote on save.
   */
  public creditNoteHasExistingNumber(note: CreditNoteModel | null | undefined): boolean {
    const creditNoteNumber = Number(note?.creditNoteNumber);
    return !isNaN(creditNoteNumber) && creditNoteNumber > 0;
  }

  /**
   * Validates all credit notes (one POST per sequence) before external confirmation save.
   * Notes that already have a credit note number are skipped.
   */
  public validateCreditNotesBeforeSave(
    idComplaint: number,
    creditNotes: CreditNoteModel[]
  ): Observable<ValidateCreditNotesResult> {
    const notesToValidate = (creditNotes || []).filter(
      (note) =>
        note?.creditNoteLines?.length > 0 &&
        note.sequence != null &&
        !this.creditNoteHasExistingNumber(note)
    );

    if (!idComplaint || notesToValidate.length === 0) {
      return of({ canSave: true });
    }

    const requests = notesToValidate.map((note) =>
      this.validateCreditNote(this.buildValidateCreditNoteRequest(idComplaint, note))
    );

    return forkJoin(requests).pipe(
      map((responses) => this.aggregateValidateCreditNoteResponses(responses))
    );
  }

  private aggregateValidateCreditNoteResponses(responses: any[]): ValidateCreditNotesResult {
    const errorMessages: string[] = [];
    const warningMessages: string[] = [];

    responses.forEach((response) => {
      const type = this.getApiEnvelopeType(response);
      const message = this.getApiEnvelopeMessage(response);
      if (type === 'error' && message) {
        errorMessages.push(message);
      } else if (type === 'warning' && message) {
        warningMessages.push(message);
      }
    });

    if (errorMessages.length > 0) {
      return { canSave: false, errorMessage: errorMessages.join('|') };
    }
    if (warningMessages.length > 0) {
      return { canSave: false, warningMessage: warningMessages.join('|') };
    }
    return { canSave: true };
  }

  public createCreditNoteFromInvoice(invoiceNumber: number, idCreditNote: number, idComplaint: number): Observable<any> {
    const body = {
      invoiceNumber: invoiceNumber,
      idCreditNote: idCreditNote,
      idComplaint: idComplaint
    };
    return this.http.post<any>(this.API_CREATE_CREDIT_NOTE_FROM_INVOICE_URL, body);
  }

  /**
   * Normalized type from CnC API envelopes (camelCase or PascalCase, trimmed).
   */
  public getApiEnvelopeType(body: any): string {
    const t = body?.type ?? body?.Type;
    return typeof t === 'string' ? t.trim().toLowerCase() : '';
  }

  /**
   * Normalized message from CnC API envelopes (camelCase or PascalCase).
   */
  public getApiEnvelopeMessage(body: any): string | undefined {
    const m = body?.message ?? body?.Message;
    return typeof m === 'string' ? m : undefined;
  }

  /** True when the API reports a business error in the JSON body (HTTP 200 is still possible). */
  public isApiEnvelopeError(body: any): boolean {
    return this.getApiEnvelopeType(body) === 'error';
  }

  /**
   * Parses JSON from an HttpClient error callback (4xx/5xx). Supports camelCase and PascalCase.
   */
  public parseHttpFailurePayload(err: any): { type?: string; message?: string } | null {
    const raw = err?.error;
    if (raw == null) {
      return null;
    }
    let body: any = raw;
    if (typeof raw === 'string') {
      try {
        body = JSON.parse(raw);
      } catch {
        return { message: raw };
      }
    }
    if (typeof body !== 'object') {
      return null;
    }
    const typeVal = body.type ?? body.Type;
    const msgVal = body.message ?? body.Message;
    const type = typeof typeVal === 'string' ? typeVal : undefined;
    const message = typeof msgVal === 'string' ? msgVal : undefined;
    return { type, message };
  }

  /**
   * Builds processName for credit note creation error emails.
   */
  public buildCreditNoteCreationProcessName(complaintNumber: string, invoiceNumber?: number | string | null): string {
    const complaint = complaintNumber ?? '';
    if (invoiceNumber != null && invoiceNumber !== '') {
      return `Credit Note Creation. Complaint Number: ${complaint}, Invoice: ${invoiceNumber}`;
    }
    return `Credit Note Creation. Complaint Number: ${complaint}`;
  }

  /**
   * Builds processName for invoice creation error emails.
   */
  public buildInvoiceCreationProcessName(complaintNumber: string): string {
    return `Invoice Creation. Complaint Number: ${complaintNumber ?? ''}`;
  }

  public sendErrorROSSEmail(message: string, processName: string): Observable<any> {
    const body = {
      message: message,
      processName: processName,
      fullName: localStorage.getItem('fullname') || ''
    };
    return this.http.post<any>(this.API_SEND_ERROR_ROSS_EMAIL_URL, body);
  }

  /**
   * Creates invoice via complaint confirmation createInvoice endpoint.
   * @param idInvoice Complaint invoice record id (from the invoice table row)
   * @param idComplaint Complaint id
   */
  public createInvoice(idInvoice: number, idComplaint: number): Observable<any> {
    const body = {
      idInvoice: idInvoice,
      idComplaint: idComplaint
    };
    return this.http.post<any>(this.API_CREATE_INVOICE_URL, body);
  }

  /**
   * Gets list of miscellaneous adjustment codes for invoice Miscellaneous dropdown.
   * @return Observable with response containing data array of { code, description?, id? }
   */
  getMiscelaneousAdjustmentCodes(): Observable<any> {
    return this.http.get<any>(this.API_MISCELLANEOUS_ADJUSTMENT_CODES_URL);
  }

  /**
   * Gets pallets data by complaint id for internal trucker/port invoice table (table starting from partCode).
   * @param complaintId Complaint id
   * @return Observable with response (array of pallet/line data)
   */
  getPalletsDataByComplaintId(complaintId: number): Observable<any> {
    const params = new HttpParams().set('complaintId', complaintId.toString());
    return this.http.get<any>(this.API_GET_PALLETS_DATA_BY_COMPLAINT_ID, { params });
  }
}
